import React, { useState, useEffect } from 'react'

import { Layout, Card, FormLayout, TextField, TextContainer, Modal, Popover, ActionList, DataTable, Stack, RadioButton, Button } from '@shopify/polaris'

import { useAdmin } from 'context/admin';
import { MoreHoriz as MoreIcon } from '@material-ui/icons';

export default function DeliverySettings() {
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [ratePopover, setRatePopover] = useState(false);
  
  // Postal code state
  const [includePostalCodes, setIncludePostalCodes] = useState('');
  const [excludePostalCodes, setExcludePostalCodes] = useState('');
  
  // Same-day cutoff state
  const [deliveryCutoff, setDeliveryCutoff] = useState('');
  const [pickupCutoff, setPickupCutoff] = useState('');
  const [cutoffErrors, setCutoffErrors] = useState({});

  const admin = useAdmin();

  // Load postal codes and cutoffs from shop config
  useEffect(() => {
    if (admin.selectedShop && admin.selectedShop.shop_config) {
      const config = admin.selectedShop.shop_config;
      
      setIncludePostalCodes(
        config.store_delivery_include_postal_codes?.join(', ') || ''
      );
      setExcludePostalCodes(
        config.store_delivery_exclude_postal_codes?.join(', ') || ''
      );
      setDeliveryCutoff(
        config.sameday_cutoff_time_delivery !== null && config.sameday_cutoff_time_delivery !== undefined
          ? config.sameday_cutoff_time_delivery.toString()
          : ''
      );
      setPickupCutoff(
        config.sameday_cutoff_time_pickup !== null && config.sameday_cutoff_time_pickup !== undefined
          ? config.sameday_cutoff_time_pickup.toString()
          : ''
      );
    }
  }, [admin.selectedShop]);

  const handleChange = (key, val) => {
    setEditing({
      ...editing,
      [key]: val
    });
  }

  const handleEdit = (deliveryRate) => {
    setEditing(deliveryRate);
    setRatePopover(false);
  }

  const handleSave = (deliveryRate) => {
    setSaving(true);
    admin.saveDeliveryRate(deliveryRate).then(() => setSaving(false))
    setEditing(false);
    setRatePopover(false);
  }

  const handleRemove = (deliveryRate) => {
    setSaving(true);
    admin.deleteDeliveryRate(deliveryRate).then(() => setSaving(false))
  }

  const handleAddDeliveryRate = () => {
    setEditing({ name: '', inside_radius: true, radius: 0, price_cents: 0})
  }

  const savePostalCodeSettings = async () => {
    setSaving(true);
    try {
      await admin.updateStoreDeliveryPostalCodes({
        include_postal_codes: includePostalCodes
          .split(',')
          .map(c => c.trim())
          .filter(c => c),
        exclude_postal_codes: excludePostalCodes
          .split(',')
          .map(c => c.trim())
          .filter(c => c)
      });
    } catch (error) {
      console.error('Error saving postal codes:', error);
    }
    setSaving(false);
  };

  const saveSamedayCutoffs = async () => {
    const deliveryValidation = parseCutoffHour(deliveryCutoff);
    const pickupValidation = parseCutoffHour(pickupCutoff);
    const errors = {};

    if (deliveryValidation.error) errors.delivery = deliveryValidation.error;
    if (pickupValidation.error) errors.pickup = pickupValidation.error;

    setCutoffErrors(errors);
    if (Object.keys(errors).length > 0) {
      admin.setToast("Cutoff times must be whole hours between 0 and 23.");
      return;
    }

    setSaving(true);
    try {
      await admin.updateSamedayCutoffs({
        delivery_cutoff: deliveryValidation.value,
        pickup_cutoff: pickupValidation.value
      });
    } catch (error) {
      console.error('Error saving cutoff times:', error);
      setCutoffErrors({ form: error.message || 'Error saving cutoff times.' });
    }
    setSaving(false);
  };

  const parseCutoffHour = (rawValue) => {
    const value = `${rawValue}`.trim();

    if (value === '') {
      return { value: null };
    }

    if (!/^\d+$/.test(value)) {
      return { error: 'Enter a whole hour between 0 and 23, or leave blank.' };
    }

    const hour = Number(value);
    if (hour < 0 || hour > 23) {
      return { error: 'Enter a whole hour between 0 and 23, or leave blank.' };
    }

    return { value: hour };
  };

  const headers = [
    { type: 'text', label: 'Name', col: 'name', fn: (x) => x },
    { type: 'text', label: 'Distance', fn: (x) => `${x.inside_radius === false ? 'Farther than' : 'Within'} ${x.radius}km from store` },
    { type: 'numeric', label: 'Price', col: 'price_cents', fn: (x) => `$${x / 100.0}` },
    { type: 'numeric', col: null, label: '', fn: (deliveryRate) => {
      return <Popover
        active={ratePopover === deliveryRate.name}
        activator={<MoreIcon style={{cursor: 'pointer'}} onClick={() => setRatePopover(deliveryRate.name)} />}
        onClose={() => setRatePopover(false)}
      >
        <ActionList items={[
          {content: 'Edit', onAction: () => handleEdit(deliveryRate)},
          {content: 'Delete', onAction: () => handleRemove(deliveryRate), destructive: true }
        ]} />
      </Popover>
    }}
  ];

  const deliveryRates = admin.selectedShop.shop_config
    ? admin.selectedShop.shop_config.delivery_rates.sort((a, b) => a.radius - b.radius)
    : [];

  const disabled = !editing.name || !editing.radius || !editing.price_cents;

  return (
    <Layout.AnnotatedSection
      title="Store Powered Delivery"
      description="Deliver to customers yourself."
    >
      <Card>
        <Card.Header
          actions={[
            { content: 'Add', onAction: handleAddDeliveryRate },
          ]}
          title="Delivery Rates"
        >
        </Card.Header>
        { deliveryRates.length > 0 ?
          <DataTable
            columnContentTypes={headers.map(h => h.type)}
            headings={headers.map(h => h.label)}
            rows={deliveryRates.map(order => headers.map(header => header.fn(header.col ? order[header.col] : order)))}
          />
          :
          <Card.Section>
            <TextContainer>
              Add delivery rates with prices based on distance from your store.
            </TextContainer>
          </Card.Section>
        }
      </Card>

      <Card>
        <Card.Header title="Postal Code / Zip Code Filtering" />
        <Card.Section>
          <TextContainer>
            <p>
              Control which postal codes are eligible for store-powered delivery. 
              Leave both fields empty to allow all postal codes.
            </p>
          </TextContainer>
          <FormLayout>
            <TextField
              label="Postal Code / Zip Code Whitelist"
              helpText="Only these postal/zip codes will be eligible for store delivery. Comma separated (e.g., M5V, M6G, 10001)"
              value={includePostalCodes}
              onChange={setIncludePostalCodes}
            />
            
            <TextField
              label="Postal Code / Zip Code Blacklist"
              helpText="These postal/zip codes will be excluded from store delivery. Comma separated."
              value={excludePostalCodes}
              onChange={setExcludePostalCodes}
            />
            
            <Button primary onClick={savePostalCodeSettings} loading={saving}>
              Save Postal Code Settings
            </Button>
          </FormLayout>
        </Card.Section>
      </Card>

      <Card>
        <Card.Header title="Same-Day Window Cutoff Times" />
        <Card.Section>
          <TextContainer>
            <p>
              Configure optional cutoff times for same-day windows. After this time, 
              same-day windows will no longer be offered (regardless of lead time settings). 
              Leave empty to disable cutoff.
            </p>
          </TextContainer>
          <FormLayout>
            <TextField
              label="Delivery Same-Day Cutoff (24-hour format)"
              type="number"
              min={0}
              max={23}
              placeholder="e.g., 14 for 2:00 PM"
              helpText="Hour when same-day delivery windows stop being available"
              value={deliveryCutoff}
              error={cutoffErrors.delivery}
              onChange={(value) => {
                setDeliveryCutoff(value);
                if (cutoffErrors.delivery || cutoffErrors.form) {
                  setCutoffErrors({ ...cutoffErrors, delivery: null, form: null });
                }
              }}
            />
            
            <TextField
              label="Pickup Same-Day Cutoff (24-hour format)"
              type="number"
              min={0}
              max={23}
              placeholder="e.g., 16 for 4:00 PM"
              helpText="Hour when same-day pickup windows stop being available"
              value={pickupCutoff}
              error={cutoffErrors.pickup || cutoffErrors.form}
              onChange={(value) => {
                setPickupCutoff(value);
                if (cutoffErrors.pickup || cutoffErrors.form) {
                  setCutoffErrors({ ...cutoffErrors, pickup: null, form: null });
                }
              }}
            />
            
            <Button primary onClick={saveSamedayCutoffs} loading={saving}>
              Save Cutoff Times
            </Button>
          </FormLayout>
        </Card.Section>
      </Card>

      <Modal
        title="Delivery Rate"
        open={editing}
        onClose={() => setEditing(false)}
        secondaryActions={[{ content: 'Cancel', onAction: () => setEditing(false) }]}
        primaryAction={{content: 'Save', loading: saving, onAction: () => handleSave(editing), disabled: disabled || saving}}>
        <Modal.Section>
          <FormLayout>
            <TextField
              label="Name"
              type="text"
              min={0}
              value={`${editing.name}`}
              onChange={(val) => handleChange('name', val)}
            />

            <Stack>
              <RadioButton
                label="Customer is within this area."
                checked={editing.inside_radius !== false}
                id="within_radius"
                name="accounts"
                onChange={(val) => handleChange('inside_radius', true)}
              />
              <RadioButton
                label="Customer is outside this area."
                id="outside_radius"
                name="accounts"
                checked={editing.inside_radius === false}
                onChange={(val) => handleChange('inside_radius', false)}
              />
            </Stack>

            <TextField
              label="Distance From Store (km)"
              type="number"
              min={0}
              value={`${editing.radius}`}
              onChange={(val) => handleChange('radius', +val)}
            />

            <TextField
              label="Price ($)"
              type="number"
              min={0}
              value={`${editing.price_cents / 100.0}`}
              onChange={(val) => handleChange('price_cents', (+val * 100))}
            />
          </FormLayout>
        </Modal.Section>
      </Modal>
    </Layout.AnnotatedSection>
  )
}
